/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { cssMap, jsx } from '@compiled/react';

import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { PROMPT_CARD_ICON_SIZE } from './thirdPartyPromptCardDimensions.ts';

const TITLE_PLACEHOLDER_WIDTH = '60%';
const TITLE_PLACEHOLDER_HEIGHT = 12;

// Matches the card's 68px border box. Local because styles cannot import values.
const CARD_HEIGHT = 68;

const styles = cssMap({
	// Matches the resolved card's box, so choosing among several products shifts nothing.
	card: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		width: '100%',
		padding: token('space.100'),
		backgroundColor: token('elevation.surface'),
		borderColor: token('color.border'),
		borderStyle: 'dashed',
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
		boxSizing: 'border-box',
	},
	title: {
		flex: 1,
	},
});

// Holds the prompt card's place while several products wait for the document chooser. No artwork,
// because the stacks are per product. `aria-hidden` hides it from accessibility tools.
export const ThirdPartyRelatedContentPromoCardSkeleton = ({
	testId = 'third-party-related-content-promo-card-skeleton',
}: {
	testId?: string;
}): React.JSX.Element => {
	// Shimmer only after hydration; an animation in the server HTML would mismatch. The hook stays
	// correct through streaming server render, where a plain `useEffect` can report the wrong phase.
	const isShimmering = !useIsSSROrHydrating();

	// A div, not a Flex: xcss rejects the border and box-sizing needed to match the card.
	return (
		<div css={styles.card} style={{ height: `${CARD_HEIGHT}px` }} aria-hidden data-testid={testId}>
			<Skeleton
				width={`${PROMPT_CARD_ICON_SIZE}px`}
				height={`${PROMPT_CARD_ICON_SIZE}px`}
				borderRadius={token('radius.small')}
				isShimmering={isShimmering}
				testId={`${testId}-icon`}
			/>
			<div css={styles.title}>
				<Skeleton
					width={TITLE_PLACEHOLDER_WIDTH}
					height={`${TITLE_PLACEHOLDER_HEIGHT}px`}
					borderRadius={token('radius.small')}
					isShimmering={isShimmering}
					testId={`${testId}-title`}
				/>
			</div>
		</div>
	);
};
